import axios from 'axios'
import { useCallback, useContext, useEffect, useState } from 'react'

import {
  getCustomerProductsQuery,
  getCustomerQuery,
} from '@data/shopify/storefront/queries/customer'
import {
  type GetCustomerOrdersQuery,
  type GetCustomerProductsQuery,
  type GetCustomerProductsQueryVariables,
  type GetCustomerQuery,
  type GetCustomerQueryVariables,
  type MailingAddressFragmentFragment,
  type OrderFinancialStatus,
  type OrderFragmentFragment,
  type OrderFulfillmentStatus,
  type OrderProductFragmentFragment,
} from '@data/shopify/storefront/types'
import { type StoredUser, useUser } from './auth'
import { getLocaleVariable } from './environment'
import { type Locale } from './language'
import { getUserDigitalProducts } from './product'
import { ShopContext } from './shop-context'
import { parseMailingAddressValues } from './shopify/address'
import {
  getShopifyDomain,
  getShopifyStorefrontApiUrl,
  parseShopifyGlobalId,
} from './shopify/client'
import {
  type ShopifyClient,
  getShopifyStorefrontClient,
} from './shopify/graphql/client'
import { getShopifyUserOrders } from './shopify/graphql/customer'
import { parseOrderNode } from './shopify/order'
import { type RechargeHeaders } from './shopify/types'

interface RechargeProperty {
  name: string
  value: string
}

interface RechargeAnalyticsUtmParam {
  utm_campaign: string
  utm_content: string
  utm_data_source: string
  utm_medium: string
  utm_source: string
  utm_term: string
  utm_time_stamp: string
}

interface RechargeAnalyticsData {
  utm_params: RechargeAnalyticsUtmParam[]
}

interface RechargeCustomer {
  accepts_marketing: boolean | null
  analytics_data: RechargeAnalyticsData
  billing_address1: string
  billing_address2: string
  billing_city: string
  billing_company: string | null
  billing_country: string
  billing_phone: string | null
  billing_province: string | null
  billing_zip: string
  created_at: string
  email: string
  first_charge_processed_at: string
  first_name: string
  has_card_error_in_dunning: boolean
  has_valid_payment_method: boolean
  hash: string
  id: number
  last_name: string
  number_active_subscriptions: number
  number_subscriptions: number
  processor_type: string | null
  reason_payment_method_not_valid: string | null
  shopify_customer_id: string
  status: string
  updated_at: string
}

interface RechargeCustomers {
  customers: RechargeCustomer[]
}

interface RechargeSubscription {
  id: number
  address_id: number
  analytics_data: RechargeAnalyticsData
  cancellation_reason: string | null
  cancellation_reason_comments: string | null
  cancelled_at: string | null
  charge_interval_frequency: string
  created_at: string
  customer_id: number
  email: string
  expire_after_specific_number_of_charges: number | null
  has_queued_charges: number
  is_prepaid: boolean
  is_skippable: boolean
  is_swappable: boolean
  max_retries_reached: number
  next_charge_scheduled_at: string
  order_day_of_month: string | null
  order_day_of_week: string | null
  order_interval_frequency: string
  order_interval_unit: RechargeOrderIntervalUnit
  price: number
  product_title: string
  properties: RechargeProperty[]
  quantity: number
  recharge_product_id: number
  shopify_product_id: number
  shopify_variant_id: number
  sku: string | null
  sku_override: boolean
  status: RechargeSubscriptionStatus
  updated_at: string
  variant_title: string
}

interface RechargeSubscriptions {
  subscriptions: RechargeSubscription[]
}

enum RechargeOrderIntervalUnit {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
}

enum RechargeSubscriptionStatus {
  ACTIVE = 'ACTIVE',
  CANCELLED = 'CANCELLED',
  EXPIRED = 'EXPIRED',
}

export interface AddressFormValues {
  firstName: string
  lastName: string
  company: string
  address1: string
  address2: string
  city: string
  country: string
  zip: string
  phone: string
  isDefault: boolean
}

export interface UserAddress {
  id: string
  globalId?: number
  isDefault: boolean
  formatted: string[]
  values: AddressFormValues
}

export interface UserOrder {
  id: string
  date: string
  paymentStatus: OrderFinancialStatus | null
  fulfillmentStatus: OrderFulfillmentStatus
  total: number
  url: string
}

export interface UserProduct {
  id: number
  title: string
  slug: string
}

const listPageSize = 10

/**
 * Gets user orders from Shopify order query result.
 */
const parseCustomerOrders = ({ customer }: GetCustomerOrdersQuery) => {
  const orderNodes = (customer?.orders?.edges
    ?.map((edge) => edge.node as OrderFragmentFragment | undefined)
    ?.filter(Boolean) ?? []) as OrderFragmentFragment[]

  return orderNodes
    .sort(
      (orderNode1, orderNode2) =>
        Date.parse(orderNode2.processedAt) - Date.parse(orderNode1.processedAt),
    )
    .map(parseOrderNode)
}

/**
 * User order list hook.
 */
export const useUserOrderList = () => {
  const { shopifyStorefrontClient } = useContext(ShopContext)
  const { user } = useUser()

  const [orders, setOrders] = useState<UserOrder[]>([])
  const [orderCursor, setOrderCursor] = useState<string | null>(null)
  const [hasMoreOrders, setHasMoreOrders] = useState(false)
  const [isLoadingOrders, setIsLoadingOrders] = useState(true)
  const [isInitialized, setIsInitialized] = useState(false)

  const loadOrders = useCallback(async () => {
    if (!user?.token) {
      return
    }

    if (!shopifyStorefrontClient) {
      throw new Error('Shopify Storefront API client missing')
    }

    setIsLoadingOrders(true)

    const shopifyUserOrders = await getShopifyUserOrders(
      shopifyStorefrontClient,
      user.token,
      orderCursor,
      listPageSize,
    )
    setOrders((orders) => [
      ...(orders ?? []),
      ...parseCustomerOrders(shopifyUserOrders),
    ])
    setOrderCursor(
      shopifyUserOrders.customer?.orders?.edges
        ?.map((orderEdge) => orderEdge.cursor)
        ?.pop() ?? null,
    )
    setHasMoreOrders(
      shopifyUserOrders.customer?.orders.pageInfo.hasNextPage ?? false,
    )

    setIsLoadingOrders(false)
  }, [orderCursor, shopifyStorefrontClient, user?.token])

  // Initialize order list
  useEffect(() => {
    if (!isInitialized && user?.token) {
      // Load first page
      loadOrders()
      setIsInitialized(true)
    }

    if (isInitialized && !user?.token) {
      // Clear orders
      setOrders([])
      setOrderCursor(null)
      setHasMoreOrders(false)
      setIsInitialized(false)
    }
  }, [isInitialized, loadOrders, user?.token])

  return {
    orders,
    hasMoreOrders,
    loadOrders,
    isLoadingOrders,
  }
}

/**
 * User product list hook.
 */
export const useUserProductList = () => {
  const { productCatalogue } = useContext(ShopContext)
  const { user } = useUser()

  const [products, setProducts] = useState<UserProduct[]>([])
  const [isLoadingProducts, setIsLoadingProducts] = useState(true)
  const [isInitialized, setIsInitialized] = useState(false)

  const loadProducts = useCallback(
    (userProductIds: number[], userSubscriptionProductIds: number[]) => {
      setIsLoadingProducts(true)

      // Get all digital products owned by the user (including products from subscriptions)
      const userDigitalProducts = getUserDigitalProducts(
        productCatalogue,
        userProductIds,
        userSubscriptionProductIds,
      )

      setProducts(userDigitalProducts)
      setIsLoadingProducts(false)
    },
    [productCatalogue],
  )

  // Initialize product list
  useEffect(() => {
    if (!isInitialized && user?.token) {
      const userProductIds = user?.ownedProducts?.map((product) => product.id)

      if (userProductIds) {
        loadProducts(userProductIds, user?.activeSubscriptionProductIds ?? [])
        setIsInitialized(true)
      }
    }

    if (isInitialized && !user?.token) {
      // Clear products
      setProducts([])
      setIsInitialized(false)
    }
  }, [isInitialized, loadProducts, user])

  return {
    products,
    productsPerPage: listPageSize,
    isLoadingProducts,
  }
}

/**
 * Gets Shopify user addresses.
 */
const getShopifyUserAddresses = async (
  shopifyStorefrontClient: ShopifyClient,
  userToken: string,
) => {
  try {
    const getCustomerResult = await shopifyStorefrontClient.query<
      GetCustomerQuery,
      GetCustomerQueryVariables
    >({
      query: getCustomerQuery,
      variables: {
        customerAccessToken: userToken,
      },
    })

    return getCustomerResult.data
  } catch (error) {
    console.log(error)

    return {
      customer: null,
    }
  }
}

/**
 * Parses Shopify user address results.
 */
const parseCustomerAddresses = (result: GetCustomerQuery) => {
  const defaultAddressId = result.customer?.defaultAddress?.id
  const addresses =
    result.customer?.addresses?.edges?.map((edge) => {
      if (!edge.node) {
        return
      }

      const addressFragment = edge.node as MailingAddressFragmentFragment

      return {
        id: addressFragment.id,
        globalId: parseShopifyGlobalId(addressFragment.id),
        isDefault: addressFragment.id === defaultAddressId,
        formatted: addressFragment.formatted,
        values: parseMailingAddressValues(addressFragment, defaultAddressId),
      }
    }) ?? []

  return addresses.filter(Boolean) as UserAddress[]
}

/**
 * Gets customer products from Shopify.
 */
const getShopifyUserProducts = async (
  shopifyStorefrontClient: ShopifyClient,
  userToken: string,
) => {
  try {
    const getCustomerProductsResult = await shopifyStorefrontClient.query<
      GetCustomerProductsQuery,
      GetCustomerProductsQueryVariables
    >({
      query: getCustomerProductsQuery,
      variables: {
        customerAccessToken: userToken,
      },
    })

    return getCustomerProductsResult.data
  } catch (error) {
    console.log(error)

    return {
      customer: null,
    }
  }
}

/**
 * Parses Shopify user products.
 */
const parseShopifyUserProducts = ({ customer }: GetCustomerProductsQuery) => {
  const products = (customer?.orders?.edges
    ?.map((edge) => {
      if (!edge.node) {
        return []
      }

      const orderProductFragment = edge.node as OrderProductFragmentFragment

      return orderProductFragment.lineItems.edges
    })
    ?.flat()
    ?.map((edge) => {
      if (!edge?.node) {
        return
      }

      return (
        edge.node.variant?.product && {
          id: parseShopifyGlobalId(edge.node.variant.product.id),
          title: edge.node.variant.product.title,
          slug: edge.node.variant.product.handle,
        }
      )
    })
    ?.filter(Boolean) ?? []) as UserProduct[]

  // Filter duplicate entries
  const productIds = products.map((product) => product.id)
  return products.filter(
    (product, index) => productIds.indexOf(product.id) === index,
  )
}

/**
 * Gets user subscriber details.
 */
const getUserSubscriberDetails = async (
  rechargeHeaders: RechargeHeaders,
  email: string,
) => {
  try {
    if (rechargeHeaders['X-Recharge-Access-Token']) {
      // Get Recharge user hash by email
      const response = await axios.get<RechargeCustomers>(
        'https://api.rechargeapps.com/customers',
        {
          params: {
            email,
          },
          headers: rechargeHeaders,
        },
      )

      return response.data.customers?.[0]
    }
  } catch (error) {
    console.log(error)
  }
}

/**
 * Gets user's active subscriptions.
 */
const getUserActiveSubscriptions = async (
  rechargeHeaders: RechargeHeaders,
  subscriberId: number,
) => {
  try {
    const response = await axios.get<RechargeSubscriptions>(
      'https://api.rechargeapps.com/subscriptions',
      {
        params: {
          customer_id: subscriberId,
          status: RechargeSubscriptionStatus.ACTIVE,
        },
        headers: rechargeHeaders,
      },
    )

    return response.data.subscriptions
  } catch (_) {
    return []
  }
}

/**
 * Gets user address, product and subscription details.
 */
export const getUserDetails = async (
  locale: Locale,
  rechargeHeaders: RechargeHeaders,
  user: StoredUser,
): Promise<StoredUser> => {
  if (!user.token || !user.email) {
    return {
      isLoggedIn: false,
    }
  }

  // Get user data from Shopify
  const shopifyDomain = getShopifyDomain(locale)
  const shopifyStorefrontAccessToken = getLocaleVariable(
    locale,
    'NEXT_PUBLIC_SHOPIFY_API_TOKEN',
  )

  if (!shopifyDomain || !shopifyStorefrontAccessToken) {
    return {
      ...user,
      isLoggedIn: true,
    }
  }

  const shopifyStorefrontApiUrl = getShopifyStorefrontApiUrl(shopifyDomain)
  const shopifyStorefrontClient = getShopifyStorefrontClient(
    shopifyStorefrontApiUrl,
    shopifyStorefrontAccessToken,
  )

  // User addresses
  const shopifyUserAddresses = await getShopifyUserAddresses(
    shopifyStorefrontClient,
    user.token,
  )
  const addresses = parseCustomerAddresses(shopifyUserAddresses)

  // User products
  const shopifyUserProducts = await getShopifyUserProducts(
    shopifyStorefrontClient,
    user.token,
  )
  const ownedProducts = parseShopifyUserProducts(shopifyUserProducts)

  // User subscriber details
  const userSubscriberDetails = await getUserSubscriberDetails(
    rechargeHeaders,
    user.email,
  )
  const subscriberId = userSubscriberDetails?.id
  const subscriberHash = userSubscriberDetails?.hash

  // User subscription products
  const userActiveSubscriptions = subscriberId
    ? await getUserActiveSubscriptions(rechargeHeaders, subscriberId)
    : []
  const activeSubscriptionProductIds =
    userActiveSubscriptions.length > 0
      ? userActiveSubscriptions.map(
          (subscription) => subscription.shopify_product_id,
        )
      : []

  return {
    ...user,
    isLoggedIn: true,
    addresses,
    ownedProducts,
    subscriberId,
    subscriberHash,
    activeSubscriptionProductIds,
  }
}
